import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../i18n';
import { client } from '../api/client';

interface TransactionInfo {
  orderId: string;
  status: string;
  amount: number;
  tokens: number;
  createdAt: string;
  errorCode?: string;
  errorMessage?: string;
}

// Map bank error codes to user-friendly messages
const errorMessages: Record<string, { sr: string; en: string }> = {
  '001': { sr: 'Transakcija je odbijena od strane banke.', en: 'Transaction was declined by the bank.' },
  '002': { sr: 'Kartica je istekla.', en: 'Card has expired.' },
  '003': { sr: 'Nedovoljno sredstava na kartici.', en: 'Insufficient funds on card.' },
  '004': { sr: 'Neispravni podaci kartice.', en: 'Invalid card details.' },
  '005': { sr: 'Kartica je blokirana.', en: 'Card is blocked.' },
  '051': { sr: 'Nedovoljno sredstava na računu.', en: 'Insufficient funds.' },
  '101': { sr: 'Transakcija je otkazana.', en: 'Transaction was cancelled.' },
  '102': { sr: 'Isteklo vreme za unos podataka.', en: 'Data entry timeout.' },
  '103': { sr: 'Greška u autentifikaciji.', en: 'Authentication error.' },
  '116': { sr: 'Nedovoljno sredstava na kartici.', en: 'Insufficient funds on card.' },
  '117': { sr: 'Pogrešan PIN.', en: 'Incorrect PIN.' },
  '118': { sr: 'Kartica nije pronađena.', en: 'Card not found.' },
  '119': { sr: 'Transakcija nije dozvoljena.', en: 'Transaction not allowed.' },
  '121': { sr: 'Prekoračen limit.', en: 'Limit exceeded.' },
};

export function PaymentFailedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useI18n();
  const [transaction, setTransaction] = useState<TransactionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const orderId = searchParams.get('OrderID') || searchParams.get('orderId') || '';
  const errorCode = searchParams.get('TranCode') || searchParams.get('error') || searchParams.get('ResponseCode') || '';

  // Get error message based on code
  const getErrorMessage = (code: string): string => {
    const defaultMsg = { 
      sr: 'Transakcija nije uspela. Molimo pokušajte ponovo.', 
      en: 'Transaction failed. Please try again.' 
    };
    return errorMessages[code]?.[language] || defaultMsg[language];
  };

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const response = await client.api.fetch(`/api/payment/status?orderId=${orderId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setTransaction({
              ...data.data,
              errorCode: errorCode || data.data.errorCode,
              errorMessage: getErrorMessage(errorCode || data.data.errorCode || ''),
            });
          }
        }
      } catch (error) {
        console.error('Error fetching transaction:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
  }, [orderId, errorCode, language]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {/* Failed Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center"
        >
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </motion.div>

        <h1 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-3">
          {language === 'sr' ? 'Plaćanje neuspešno' : 'Payment Failed'}
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          {language === 'sr' 
            ? 'Nažalost, vaša transakcija nije uspela.' 
            : 'Unfortunately, your transaction could not be completed.'}
        </p>

        {/* Error Message Box */}
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-medium text-red-700 dark:text-red-400">
              {errorCode ? `Kod greške: ${errorCode}` : (language === 'sr' ? 'Greška' : 'Error')}
            </span>
          </div>
          <p className="text-red-700 dark:text-red-400 text-sm">
            {getErrorMessage(errorCode)}
          </p>
        </div>

        {/* Transaction Details */}
        {loading ? (
          <div className="animate-pulse bg-slate-100 dark:bg-slate-700 rounded-xl p-6 mb-6">
            <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-1/2 mx-auto"></div>
          </div>
        ) : transaction ? (
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mb-6 text-left">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
              {language === 'sr' ? 'Detalji transakcije:' : 'Transaction Details:'}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-1 border-b border-slate-200 dark:border-slate-600">
                <span className="text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Paket:' : 'Package:'}
                </span>
                <span className="text-slate-700 dark:text-slate-300">
                  {transaction.tokens} tokena
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-200 dark:border-slate-600">
                <span className="text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Iznos:' : 'Amount:'}
                </span>
                <span className="text-slate-700 dark:text-slate-300">
                  {transaction.amount.toLocaleString()} RSD
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Status:' : 'Status:'}
                </span>
                <span className="text-red-600 dark:text-red-400 font-medium">
                  {language === 'sr' ? '✗ Odbijeno' : '✗ Declined'}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Order ID */}
        {orderId && (
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 mb-6">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Broj narudžbine:' : 'Order ID:'}
            </p>
            <p className="font-mono text-sm text-slate-700 dark:text-slate-300">{orderId}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/kupi-tokene')}
            className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
          >
            {language === 'sr' ? 'Pokušaj ponovo' : 'Try Again'}
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 px-6 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-xl transition-colors"
          >
            {language === 'sr' ? 'Vrati se na početnu' : 'Back to Home'}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-xs text-blue-700 dark:text-blue-400">
            <span className="font-medium">
              {language === 'sr' ? '💡 Šta možete učiniti:' : '💡 What you can do:'}
            </span>
          </p>
          <ul className="text-xs text-blue-600 dark:text-blue-300 mt-2 space-y-1 text-left list-disc list-inside">
            <li>{language === 'sr' ? 'Proverite da li kartica ima dovoljno sredstava' : 'Check if your card has sufficient funds'}</li>
            <li>{language === 'sr' ? 'Pokušajte sa drugom karticom' : 'Try with a different card'}</li>
            <li>{language === 'sr' ? 'Kontaktirajte vašu banku ako problem potraje' : 'Contact your bank if the issue persists'}</li>
          </ul>
        </div>

        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          {language === 'sr' 
            ? 'Sredstva sa vaše kartice nisu naplaćena.' 
            : 'Your card was not charged.'}
        </p>
      </motion.div>
    </div>
  );
}
